import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useResetPassword } from "./auth";
import { AuthShell } from "./authChrome";

export function ResetPasswordPage() {
  const { t } = useTranslation("auth");
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const reset = useResetPassword();

  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // No token in the URL → the link was opened wrong; nothing to submit.
  if (!token) {
    return (
      <AuthShell backTo="/login" backLabel={t("forgot.back_to_login")}>
        <div className="indigo-card-glass p-6 space-y-4 text-center">
          <div className="text-[16px] font-semibold text-fg-primary">{t("reset.title")}</div>
          <p className="text-[13px] text-fg-secondary leading-relaxed">{t("reset.err_no_token")}</p>
          <Link
            to="/forgot-password"
            className="block w-full h-11 leading-[2.75rem] rounded-pill bg-accent hover:bg-accent-hover text-accent-on font-semibold transition-colors"
          >
            {t("reset.request_new")}
          </Link>
        </div>
      </AuthShell>
    );
  }

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (pw.length < 8) {
      setError(t("reset.err_too_short"));
      return;
    }
    if (pw !== pw2) {
      setError(t("reset.err_mismatch"));
      return;
    }
    reset.mutate(
      { token, new_password: pw },
      {
        onSuccess: () => setDone(true),
        onError: (err: any) => {
          const status = err?.response?.status;
          // 400 = invalid/expired token, 422 = too short (already guarded above).
          if (status === 400) setError(t("reset.err_invalid_token"));
          else if (status === 422) setError(t("reset.err_too_short"));
          else setError(t("reset.error"));
        },
      },
    );
  };

  return (
    <AuthShell backTo="/login" backLabel={t("forgot.back_to_login")}>
      <div className="indigo-card-glass p-6">
        {done ? (
          <div className="space-y-4 text-center">
            <div className="text-[16px] font-semibold text-fg-primary">{t("reset.done_title")}</div>
            <p className="text-[13px] text-fg-secondary leading-relaxed">{t("reset.done_text")}</p>
            <Link
              to="/login"
              className="block w-full h-11 leading-[2.75rem] rounded-pill bg-accent hover:bg-accent-hover text-accent-on font-semibold transition-colors"
            >
              {t("reset.go_to_login")}
            </Link>
          </div>
        ) : (
          <>
            <div className="mb-5">
              <div className="text-[16px] font-semibold text-fg-primary">{t("reset.title")}</div>
              <p className="mt-1.5 text-[13px] text-fg-secondary leading-relaxed">{t("reset.subtitle")}</p>
            </div>
            <form onSubmit={submit} className="space-y-3" aria-label={t("reset.title")}>
              <label htmlFor="reset-pw-new" className="sr-only">{t("reset.new_password")}</label>
              <input
                id="reset-pw-new"
                type="password"
                placeholder={t("reset.new_password")}
                value={pw}
                onChange={(e) => setPw(e.target.value)}
                required
                aria-required="true"
                aria-invalid={error ? "true" : "false"}
                aria-describedby={error ? "reset-pw-error" : undefined}
                minLength={8}
                autoComplete="new-password"
                className="w-full h-11 px-3.5 rounded-pill bg-bg-surface/85 backdrop-blur border border-bg-border focus:border-accent focus:shadow-focus outline-none text-[15px] text-fg-primary placeholder:text-fg-dim transition-shadow"
              />
              <label htmlFor="reset-pw-confirm" className="sr-only">{t("reset.confirm_password")}</label>
              <input
                id="reset-pw-confirm"
                type="password"
                placeholder={t("reset.confirm_password")}
                value={pw2}
                onChange={(e) => setPw2(e.target.value)}
                required
                aria-required="true"
                aria-invalid={error ? "true" : "false"}
                aria-describedby={error ? "reset-pw-error" : undefined}
                minLength={8}
                autoComplete="new-password"
                className="w-full h-11 px-3.5 rounded-pill bg-bg-surface/85 backdrop-blur border border-bg-border focus:border-accent focus:shadow-focus outline-none text-[15px] text-fg-primary placeholder:text-fg-dim transition-shadow"
              />
              {error && (
                <div id="reset-pw-error" role="alert" className="text-[13px] text-bad bg-bad-subtle/30 border border-bad/30 rounded-pill px-3 py-2 leading-snug">
                  {error}
                </div>
              )}
              <button
                type="submit"
                disabled={reset.isPending}
                className="w-full h-11 rounded-pill bg-accent hover:bg-accent-hover text-accent-on font-semibold disabled:opacity-50 transition-colors"
              >
                {reset.isPending ? t("reset.saving") : t("reset.submit")}
              </button>
            </form>
          </>
        )}
      </div>
    </AuthShell>
  );
}
